import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import { Balance, InfoCircledIcon, Text, toolkitCx, Tooltip } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useMemo } from "react";

import { joyrideSelectors } from "../../../constants/joyride.constants";
import { Status } from "../../../gql/generated/taste-test.sdk";
import { useCalculateContributionOutcome } from "../../../hooks/taste-test/useCalculateContributionOutcome";
import { useCurrentTasteTestContribution } from "../../../hooks/taste-test/useCurrentTasteTestContribution";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useProfileAppContext } from "../../../stores";
import { IAssetMetaData } from "../../../types/Asset.types";
import { getAssetName } from "../../../utils/assets.utils";
import FadeInBasic from "../../FadeInView/FadeInBasic";
const {
  theme: { colors },
} = require("@sundae/tailwind-config");

const defaultSecondaryColor = colors.secondary["DEFAULT"];

const TasteTestStatusAndPrice: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    derived: { hasContribution },
    state: {
      tasteTest,
      tasteTestStatus: { isTasteTestInactive },
      tasteTestPersonalization,
    },
  } = useTasteTestContext();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const { calculateTargetPrice } = useCalculateContributionOutcome({
    currentBalance: tasteTest?.currentBalance,
  });
  const currentContribution = useCurrentTasteTestContribution();
  const targetPrice = calculateTargetPrice();

  const statusTextVariant = useMemo(() => {
    switch (tasteTest?.status) {
      case Status.Ongoing:
        return "success";
      case Status.LastCall:
        return "info";
      case Status.Concluded:
      case Status.Closing:
        return "warning";
      case Status.Geofenced:
      case Status.Moderated:
        return "error";
      default:
        return "normal";
    }
  }, [tasteTest?.status]);

  if (!tasteTest) return null;

  return (
    <div className="flex w-fit flex-wrap items-center gap-4 lg:ml-auto lg:w-full lg:flex-1 lg:justify-end">
      {/* -------------------------------------------------------------------------------------------------
       * TT Status
       * -----------------------------------------------------------------------------------------------*/}
      <div
        className={cx("grid rounded-lg", toolkitCx.layout.spacing.both.md, {
          "bg-success/10": tasteTest?.status === Status.Ongoing,
          "bg-primary/10": tasteTest?.status === Status.LastCall,
          "bg-warning-300/10":
            [Status.Concluded, Status.Closing].includes(tasteTest.status) || isTasteTestInactive,
          "bg-error/10": [Status.Geofenced, Status.Moderated].includes(tasteTest.status),
        })}
        data-guide-selector={joyrideSelectors.guides.tasteTest.interactiveGuide.status}
      >
        <Text tag="h3" variant={statusTextVariant} size="lg" weight="medium">
          {/** Add a space for camelCase (i.e. LastCall) */}
          {t(`status.${tasteTest?.status}`)}
        </Text>

        <Text tag="span" size="xs" variant="muted">
          {t("status", { ns: "glossary" })}
          {isTasteTestInactive && (
            <Tooltip
              trigger={
                <InformationCircleIcon className="ml-1 inline h-3 w-3 cursor-help !text-current" />
              }
              tooltipLabel={t(`status.${tasteTest?.status ?? "Concluded"}`, {
                context: "tooltip",
              })}
            />
          )}
        </Text>
      </div>

      {/* -------------------------------------------------------------------------------------------------
       * Target Price
       * -----------------------------------------------------------------------------------------------*/}
      <div
        className={cx("grid rounded-lg bg-secondary/10", toolkitCx.layout.spacing.both.md)}
        data-guide-selector={joyrideSelectors.guides.tasteTest.interactiveGuide.price}
      >
        <div className="flex h-fit items-baseline gap-1">
          <div style={{ color: tasteTestPersonalization?.secondaryColor ?? defaultSecondaryColor }}>
            <Text tag="h3" variant="current" size="lg" weight="medium">
              {targetPrice.display}
            </Text>
          </div>

          <Text tag="span" size="2xs" variant="silent">
            {t("per", { ns: "common" })}{" "}
            {getAssetName(
              (tasteTest?.asset as unknown as IAssetMetaData) ?? ({} as IAssetMetaData)
            )}
          </Text>
        </div>

        <Text tag="span" size="xs" variant="muted">
          {isTasteTestInactive ? t("price", { ns: "common" }) : t("detail.kpis.targetPrice")}
          <Tooltip
            trigger={
              <InformationCircleIcon className="ml-1 inline h-3 w-3 cursor-help !text-current" />
            }
            // TODO: taste-test; tooltip
            tooltipLabel={t("detail.kpis.targetPrice", { context: "tooltip" })}
          />
        </Text>
      </div>

      {hasContribution && (
        <>
          <FadeInBasic
            className={cx("grid rounded-lg bg-success/10", toolkitCx.layout.spacing.both.md)}
          >
            <Balance
              value={currentContribution}
              weight="medium"
              size="lg"
              privacy={privacyEnabled}
              locale={currentLocale}
              abbreviate={false}
              showSymbol
              variant="success"
            />

            <Text tag="span" size="xs" variant="muted">
              {t("detail.widget.currentContribution")}
              <Tooltip
                trigger={
                  <InfoCircledIcon className="ml-1 inline h-3 w-3 cursor-help !text-current" />
                }
                tooltipLabel={t("detail.widget.currentContribution", { context: "tooltip" })}
              />
            </Text>
          </FadeInBasic>
        </>
      )}
    </div>
  );
};

export default TasteTestStatusAndPrice;
